import { useEffect, useRef, useState } from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import { toDatePickerValue } from "utils/formDateValue";

const DISPLAY_FORMAT = "DD-MM-YYYY";

/**
 * A MUI DatePicker bound to a react-hook-form field that stores its date as a
 * DD-MM-YYYY string.
 *
 * The picker keeps its own draft of what is being typed. Half-typed input is not a
 * usable date — MUI reports the first digit of a year as invalid, for instance — and
 * feeding that back as the picker's value blanks the sections the clinician already
 * had. Holding the draft lets any section be corrected in place, while form state only
 * ever receives a complete date, or "" so the field's required rule still applies.
 */
export const FormDatePicker = ({
  form,
  name,
  emptyValue = null,
  sx = { width: "100%", height: "36px" },
  ...datePickerProps
}) => {
  const storedDate = form.watch(name);
  const [draftDate, setDraftDate] = useState(
    () => toDatePickerValue(storedDate) ?? emptyValue
  );
  /** The last string this picker wrote, to tell its own echo from an external change. */
  const lastWrittenDateRef = useRef(null);

  useEffect(() => {
    if (storedDate === lastWrittenDateRef.current) return;

    setDraftDate(toDatePickerValue(storedDate) ?? emptyValue);
  }, [storedDate, emptyValue]);

  const handleChange = (newValue) => {
    setDraftDate(newValue);

    const nextDate = dayjs(newValue);
    const nextStoredDate =
      newValue && nextDate.isValid() ? nextDate.format(DISPLAY_FORMAT) : "";

    lastWrittenDateRef.current = nextStoredDate;
    form.setValue(name, nextStoredDate);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        format={DISPLAY_FORMAT}
        name={name}
        value={draftDate}
        onChange={handleChange}
        sx={sx}
        {...datePickerProps}
      />
    </LocalizationProvider>
  );
};
